import { logger } from '@heidi/runtime/logger';
import { isTauri } from '@heidi/runtime/platform';
import {
  SessionScope,
  SessionSortBy,
  type SessionStatus,
  Shared__enums__enums__SortOrder as SortOrderEnum,
} from '@heidi/schema/ml-scribe';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  DESKTOP_SESSIONS_LIST_CACHE_KEY,
  GET_SESSIONS_LIST,
  GET_SESSIONS_LIST_FIRST_PAGE,
  type SessionListItem,
  type SessionListResponse,
  SESSIONS_PAGE_SIZE,
  withoutPendingServerDeletes,
} from '../../contracts';
import { getSessionsListV2, type SessionsListParams } from '../api/list';
import { getSessionDataConfig } from '../config';
import { SESSIONS_REFETCH_INTERVAL } from '../query/sessionsList';
import {
  type DateRange,
  isClinicianFilterApplicableForScope,
  isPatientProfileFilterApplicableForScope,
  type SortOption,
  type SortOrder,
} from './atoms';

// Canonical home is `@heidi/session/contracts` (the cache key sits in
// `session-list-storage-keys`, which logout reads); re-exported here for
// existing call sites that import from this file.
export { DESKTOP_SESSIONS_LIST_CACHE_KEY, GET_SESSIONS_LIST };

/** Distinct cache-key root for the first-page poll. Deliberately NOT prefixed
 *  with `GET_SESSIONS_LIST` so the prefix-based `setQueriesData` / `resetQueries`
 *  operations on `[GET_SESSIONS_LIST]` (status mutations, manual refresh) never
 *  match this non-paginated entry — they assume the `InfiniteData` shape.
 *  @internal Exported for testing only. */

/** Runaway guard: past this, reload clean instead of letting the poll grow page 0 forever.
 *  @internal Exported for testing only. */
export const MAX_FIRST_PAGE_ROWS = SESSIONS_PAGE_SIZE * 2;

// Persist only a bounded, default-list snapshot for desktop offline relaunch.
// React Query's global localStorage persister intentionally excludes the full
// sessions list because users can scroll many pages. Offline startup only needs
// a first-page seed so the list is usable before the backend is reachable.
// The cache key lives in `@heidi/session/contracts` (`session-list-storage-keys`)
// so `logoutStorage.ts` can clear it on logout (it holds patient PII) without
// importing this heavy hook module.

/** Map frontend camelCase sort option to backend snake_case enum.
 * Follows the same pattern as useQuerySessionsWithPagination.ts.
 * @internal Exported for testing only. */
export const SORT_BY_MAPPING: Partial<Record<SortOption, SessionSortBy>> = {
  createdAt: SessionSortBy.created_at,
  updatedAt: SessionSortBy.updated_at,
  name: SessionSortBy.name,
  // 'status' is intentionally omitted — the BE endpoint does not support sorting by status.
};

/** @internal Exported for testing only. */
export const mapSortOrder = (order: SortOrder): SortOrderEnum =>
  order === 'asc' ? SortOrderEnum.ASC : SortOrderEnum.DESC;

/** Gate the clinician filter on the *same* predicate as the UI
 *  (`isClinicianFilterApplicableForScope`) so the query and the filter row /
 *  chip / Clear button can't disagree. A selection persisted on a scope where
 *  the filter is no longer shown (e.g. a `direct_share` selection left in
 *  localStorage) must not keep silently filtering sessions the user can't see
 *  a control for.
 *  @internal Exported for testing only. */
export const getEffectiveClinicianIds = (
  scope: SessionScope,
  selectedClinicianIds: string[]
): string[] | undefined =>
  isClinicianFilterApplicableForScope(scope) && selectedClinicianIds.length > 0
    ? selectedClinicianIds
    : undefined;

/** Gate the patient-profile filter on the *same* predicate as the UI
 *  (`isPatientProfileFilterApplicableForScope`) so the query and the filter row
 *  / chip / Clear button can't disagree. A selection persisted on a scope where
 *  the filter is no longer shown (e.g. a `me` selection left in localStorage
 *  after switching to `all`) must not keep silently filtering sessions the user
 *  can't see a control for.
 *  @internal Exported for testing only. */
export const getEffectivePatientProfileIds = (
  scope: SessionScope,
  selectedPatientProfiles: string[]
): string[] | undefined =>
  isPatientProfileFilterApplicableForScope(scope) && selectedPatientProfiles.length > 0
    ? selectedPatientProfiles
    : undefined;

type DesktopSessionsListCache = Record<
  string,
  {
    savedAt: number;
    page: SessionListResponse;
  }
>;

/** @internal Exported for testing only. */
export const isCacheableDesktopSessionsList = (
  params: UseQuerySessionsListParams
): boolean => params.searchQuery === '' && !params.isUrgentOnly;

const normalizeDesktopSessionsListFilterValues = (values: string[]) =>
  values.length > 0 ? [...values].sort().join(',') : '-';

/** Cap on how many *filtered* buckets we retain in the single cache key. The
 *  unfiltered per-scope seeds (the primary offline-relaunch snapshot the feature
 *  exists for) are never counted or evicted; only filter combinations are, oldest
 *  by `savedAt` first, so exploring many filters can't grow the key until
 *  `setItem` throws on quota and silently disables all cache writes.
 *  @internal Exported for testing only. */
export const MAX_DESKTOP_SESSIONS_LIST_FILTER_BUCKETS = 20;

// A filter suffix always contains `key=value` segments; a base (unfiltered)
// bucket `${scope}:${sortBy}:${sortOrder}` never contains `=`.
const isFilteredDesktopSessionsListBucketKey = (key: string): boolean =>
  key.includes('=');

const isUnfilteredDesktopSessionsList = (params: UseQuerySessionsListParams): boolean =>
  params.selectedStatuses.length === 0 &&
  !params.selectedDateRange.from &&
  !params.selectedDateRange.to &&
  !getEffectivePatientProfileIds(params.scope, params.selectedPatientProfiles) &&
  !getEffectiveClinicianIds(params.scope, params.selectedClinicianIds);

const getDesktopSessionsListFilterBucket = (params: UseQuerySessionsListParams) =>
  [
    `status=${normalizeDesktopSessionsListFilterValues(params.selectedStatuses)}`,
    `from=${params.selectedDateRange.from?.toISOString() ?? '-'}`,
    `to=${params.selectedDateRange.to?.toISOString() ?? '-'}`,
    `patients=${normalizeDesktopSessionsListFilterValues(
      getEffectivePatientProfileIds(params.scope, params.selectedPatientProfiles) ?? []
    )}`,
    `clinicians=${normalizeDesktopSessionsListFilterValues(
      getEffectiveClinicianIds(params.scope, params.selectedClinicianIds) ?? []
    )}`,
  ].join('|');

const getDesktopSessionsListCacheBucket = (params: UseQuerySessionsListParams) => {
  const baseBucket = `${params.scope}:${params.sortBy}:${params.sortOrder}`;
  return isUnfilteredDesktopSessionsList(params)
    ? baseBucket
    : `${baseBucket}:${getDesktopSessionsListFilterBucket(params)}`;
};

const getFallbackDesktopSessionsListCacheBuckets = (
  params: UseQuerySessionsListParams
): string[] => {
  const currentBucket = getDesktopSessionsListCacheBucket(params);
  if (!isUnfilteredDesktopSessionsList(params)) return [currentBucket];
  return [
    currentBucket,
    `${SessionScope.me}:${params.sortBy}:${params.sortOrder}`,
    `${SessionScope.all}:${params.sortBy}:${params.sortOrder}`,
    `${SessionScope.direct_share}:${params.sortBy}:${params.sortOrder}`,
  ];
};

const readDesktopSessionsListCacheMap = (): DesktopSessionsListCache => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(DESKTOP_SESSIONS_LIST_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as DesktopSessionsListCache;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/** @internal Exported for testing only. */
export const readDesktopSessionsListCache = (
  params: UseQuerySessionsListParams
): InfiniteData<SessionListResponse> | undefined => {
  if (!isTauri() || !isCacheableDesktopSessionsList(params)) return undefined;
  const cache = readDesktopSessionsListCacheMap();
  const cached = getFallbackDesktopSessionsListCacheBuckets(params)
    .map((bucket) => cache[bucket])
    .find(Boolean);
  if (!cached) return undefined;
  // This snapshot was written while last online, so it still holds any session
  // the clinician has since deleted offline. It is read SYNCHRONOUSLY during
  // render and becomes the query's `initialData`, which makes it the seed of
  // the first painted frame after a cold offline relaunch — so a queued delete
  // has to be applied to it here, in the same synchronous pass, rather than by
  // an effect that could only remove the row a frame later.
  //
  // Read through the synchronous accessor because this is not a hook and has no
  // subscription to offer; the surfaces that must also RE-render on a later
  // enqueue subscribe to the mirror themselves.
  return {
    pages: [
      {
        ...cached.page,
        sessions: withoutPendingServerDeletes(
          cached.page.sessions,
          getSessionDataConfig().readOfflinePendingServerDeleteIds()
        ),
      },
    ],
    pageParams: [undefined],
  };
};

// Drop the oldest filter buckets (by `savedAt`) once we exceed the retention
// cap. Unfiltered per-scope seeds are left untouched so the offline-relaunch
// snapshot always survives.
const evictOldestDesktopSessionsListFilterBuckets = (
  cache: DesktopSessionsListCache
): void => {
  const filterKeys = Object.keys(cache).filter(isFilteredDesktopSessionsListBucketKey);
  if (filterKeys.length <= MAX_DESKTOP_SESSIONS_LIST_FILTER_BUCKETS) return;
  filterKeys
    .sort((a, b) => cache[a]!.savedAt - cache[b]!.savedAt)
    .slice(0, filterKeys.length - MAX_DESKTOP_SESSIONS_LIST_FILTER_BUCKETS)
    .forEach((key) => delete cache[key]);
};

/** @internal Exported for testing only. */
export const writeDesktopSessionsListCache = (
  params: UseQuerySessionsListParams,
  data: InfiniteData<SessionListResponse> | undefined
): void => {
  if (!isTauri() || !isCacheableDesktopSessionsList(params)) return;
  const firstPage = data?.pages[0];
  if (!firstPage) return;

  try {
    const cache = readDesktopSessionsListCacheMap();
    cache[getDesktopSessionsListCacheBucket(params)] = {
      savedAt: Date.now(),
      page: firstPage,
    };
    evictOldestDesktopSessionsListFilterBuckets(cache);
    window.localStorage.setItem(DESKTOP_SESSIONS_LIST_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Best-effort only: a full/blocked localStorage must not break the list.
  }
};

export function flattenSessionListPages(
  pages: SessionListResponse[] | undefined
): SessionListItem[] {
  // `?? []` inside the flatMap as well as outside it: a page that arrives
  // without a `sessions` array would otherwise be appended as an `undefined`
  // row rather than flattened away, and every consumer of these rows
  // dereferences it (SCR-7433).
  return pages?.flatMap((page) => page.sessions ?? []) ?? [];
}

/** Merge a freshly-polled first page into the infinite-query cache by upsert:
 *  rows already loaded (on any page) are patched in place; page 0 is rebuilt
 *  in the polled page's order, so new rows and rows that moved within page 0
 *  follow the server. A polled row living on a deeper page stays there until
 *  the next full refetch (see the exclusion below). Page-0 rows the poll
 *  omitted are ambiguous — displaced off the window by new arrivals, or
 *  removed outright (deleted, filtered out, rescheduled past the group's
 *  bounds). A complete poll (`next_cursor: null`) proves removal, so they are
 *  dropped; otherwise each keeps its place relative to the page-0 row that
 *  followed it, so a possibly-removed row holds its rank instead of sinking
 *  to the tail (the visual top of a reversed section). Deeper pages keep
 *  their cursors so `fetchNextPage` keeps chaining.
 *  @internal Exported for testing only. */
export function mergeFirstPageIntoList(
  existing: InfiniteData<SessionListResponse> | undefined,
  firstPage: SessionListResponse
): InfiniteData<SessionListResponse> | undefined {
  if (!existing?.pages.length) return existing;
  // Keyed by id so a repeated session_id in the polled page collapses to one
  // row; `values()` keeps first-occurrence order.
  const freshById = new Map(firstPage.sessions.map((s) => [s.session_id, s]));
  const seen = new Set<string>();
  const pages = existing.pages.map((page) => ({
    ...page,
    sessions: page.sessions.map((session) => {
      seen.add(session.session_id);
      return freshById.get(session.session_id) ?? session;
    }),
  }));
  const head = pages[0]!;
  const headIds = new Set(head.sessions.map((s) => s.session_id));
  // Rebuild page 0 in the polled page's order: new rows and current page-0
  // rows slot where the server put them. Polled rows living on a deeper page
  // stay there (patched above), so they are excluded here.
  const merged = [...freshById.values()].filter(
    (s) => !seen.has(s.session_id) || headIds.has(s.session_id)
  );
  if (firstPage.next_cursor != null) {
    // Ambiguous omissions: re-insert each just before the nearest page-0 row
    // that followed it and survived the poll, or at the end when nothing
    // after it survived (the displaced-off-the-window case).
    head.sessions.forEach((session, index) => {
      if (freshById.has(session.session_id)) return;
      const successor = head.sessions
        .slice(index + 1)
        .find((s) => freshById.has(s.session_id));
      const at = successor
        ? merged.findIndex((s) => s.session_id === successor.session_id)
        : merged.length;
      merged.splice(at, 0, session);
    });
  }
  pages[0] = { ...head, sessions: merged };
  return { ...existing, pages };
}

export type UseQuerySessionsListParams = {
  scope: SessionScope;
  selectedStatuses: SessionStatus[];
  selectedDateRange: DateRange;
  selectedPatientProfiles: string[];
  selectedClinicianIds: string[];
  sortBy: SortOption;
  sortOrder: SortOrder;
  searchQuery: string;
  /** When true, request only urgent sessions server-side (`is_urgent=true`). */
  isUrgentOnly: boolean;
};

/** Paginated query and first-page poll share this key's per-filter suffix but
 *  pass different `root` ids, so they are separate cache entries that still
 *  change in lock-step when a filter changes.
 *  @internal Exported for testing only. */
export const buildSessionsListQueryKey = (
  params: UseQuerySessionsListParams,
  root: string = GET_SESSIONS_LIST
) =>
  [
    root,
    params.scope,
    params.selectedStatuses,
    params.selectedDateRange.from?.toISOString(),
    params.selectedDateRange.to?.toISOString(),
    getEffectivePatientProfileIds(params.scope, params.selectedPatientProfiles),
    getEffectiveClinicianIds(params.scope, params.selectedClinicianIds),
    params.sortBy,
    params.sortOrder,
    params.searchQuery,
    params.isUrgentOnly,
  ] as const;

/** @internal Exported for testing only. */
export const buildSessionsListRequest = (
  params: UseQuerySessionsListParams,
  cursor: string | undefined
): SessionsListParams => ({
  scope: params.scope,
  page_size: SESSIONS_PAGE_SIZE,
  cursor,
  sort_by: SORT_BY_MAPPING[params.sortBy],
  sort_order: mapSortOrder(params.sortOrder),
  session_status:
    params.selectedStatuses.length > 0 ? params.selectedStatuses : undefined,
  date_from: params.selectedDateRange.from?.toISOString(),
  date_to: params.selectedDateRange.to?.toISOString(),
  patient_profile_ids: getEffectivePatientProfileIds(
    params.scope,
    params.selectedPatientProfiles
  ),
  clinician_ids: getEffectiveClinicianIds(params.scope, params.selectedClinicianIds),
  q: params.searchQuery || undefined,
  // Urgency filter is server-side now: send `true` to get only urgent sessions,
  // omit otherwise (no urgency filter). `sort_by=urgency` stays client-side.
  is_urgent: params.isUrgentOnly ? true : undefined,
});

/** Polls ONLY the first page on the refetch interval and merges it into the
 *  infinite-query cache. This keeps the newest team activity fresh with a
 *  single request per tick — instead of re-fetching every loaded page, which
 *  scaled linearly with scroll depth (50 pages → 50 requests every 30s). Pages
 *  2+ are owned by the infinite query and are never polled. */
function useFirstPagePoll(params: UseQuerySessionsListParams, enabled: boolean): void {
  const queryClient = useQueryClient();
  const infiniteQueryKey = buildSessionsListQueryKey(params);

  const { data: firstPage, error: pollError } = useQuery({
    // Re-derive from `params` (not `infiniteQueryKey`) to satisfy query exhaustive-deps.
    queryKey: buildSessionsListQueryKey(params, GET_SESSIONS_LIST_FIRST_PAGE),
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    refetchIntervalInBackground: false, // pause polling on a hidden tab
    // Refresh a stale first page on return without making users wait for a
    // polling tick. The freshness window prevents repeated tab switches from
    // issuing extra requests; the infinite query still never refetches on focus.
    staleTime: SESSIONS_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    enabled,
    queryFn: async (): Promise<SessionListResponse> => {
      // The poll's FIRST run (on mount, or when a group becomes the in-view
      // active group during layout churn) must not duplicate the page-0 request
      // the infinite query already issues. React Query fires a query's initial
      // fetch the moment it's enabled — `refetchInterval` only governs the
      // subsequent ticks — so without this the poll re-fetches page 0 alongside
      // the infinite query. Seed that first run from the infinite query instead:
      // reuse its cached first page, or — if that fetch is still in flight —
      // piggyback on it via `ensureInfiniteQueryData` (same key ⇒ request
      // de-duplication, so no second network call). Only the 30s
      // `refetchInterval` (and window refocus) fall through to a fresh request.
      const pollKey = buildSessionsListQueryKey(params, GET_SESSIONS_LIST_FIRST_PAGE);
      const isSeedFetch = queryClient.getQueryState(pollKey)?.data === undefined;
      if (isSeedFetch) {
        const seeded = await queryClient.ensureInfiniteQueryData({
          // Inline `buildSessionsListQueryKey(params)` (not the `infiniteQueryKey`
          // local) so the query exhaustive-deps lint sees `params` in the key —
          // same reason the poll's own `queryKey` re-derives it above. It resolves
          // to the same key as the mounted infinite query, so this de-dupes.
          queryKey: buildSessionsListQueryKey(params),
          queryFn: async ({ pageParam }): Promise<SessionListResponse> => {
            const response = await getSessionsListV2(
              buildSessionsListRequest(params, pageParam)
            );
            return response.data;
          },
          getNextPageParam: (lastPage: SessionListResponse) =>
            lastPage.next_cursor ?? undefined,
          initialPageParam: undefined as string | undefined,
          // `ensureInfiniteQueryData` returns the cached first page as-is when
          // present and fetches only when the infinite cache is empty (it ignores
          // staleness unless `revalidateIfStale` is set, which we don't) — so the
          // seed never issues its own refetch. Freshness is the poll's job, via
          // `refetchInterval` below.
        });
        const seededFirstPage = seeded.pages[0];
        if (seededFirstPage) return seededFirstPage;
      }
      const response = await getSessionsListV2(
        buildSessionsListRequest(params, undefined)
      );
      return response.data;
    },
  });

  // The poll is a background freshness mechanism — its failures don't surface to
  // the list's `error` state, so log them to stay diagnosable during an outage.
  // Log only the message: the raw AxiosError carries the auth header and the `q`
  // patient-search term in `config`, which would ship to Datadog (the redact
  // paths don't cover the Datadog send path).
  useEffect(() => {
    if (pollError) {
      logger.warn(
        'Sessions first-page poll failed:',
        pollError instanceof Error ? pollError.message : String(pollError)
      );
    }
  }, [pollError]);

  // The query key is recreated every render; hold it in a ref so the merge
  // effect can depend only on `firstPage` (a stable reference until the next
  // successful poll). Depending on the key array directly would re-run the
  // effect — and thus re-write the cache — on every render.
  const infiniteQueryKeyRef = useRef(infiniteQueryKey);
  infiniteQueryKeyRef.current = infiniteQueryKey;

  useEffect(() => {
    if (!firstPage) return;
    const key = infiniteQueryKeyRef.current;
    const existing = queryClient.getQueryData<InfiniteData<SessionListResponse>>(key);
    const merged = mergeFirstPageIntoList(existing, firstPage);
    const mergedFirstPage = merged?.pages[0];
    // Infinite query hasn't loaded yet; it will fetch a fresh page 0 itself.
    if (!mergedFirstPage) return;
    if (mergedFirstPage.sessions.length > MAX_FIRST_PAGE_ROWS) {
      logger.warn(
        'Sessions first page exceeded MAX_FIRST_PAGE_ROWS; refetching loaded pages',
        { count: mergedFirstPage.sessions.length }
      );
      // invalidate (not reset) so loaded pages refetch in the background while the
      // current data stays visible — no blank flash, no scroll jump to the top.
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
      return;
    }
    queryClient.setQueryData(key, merged);
  }, [firstPage, queryClient]);
}

export type UseQuerySessionsListOptions = {
  /**
   * Gate the 30s first-page poll independently of the query itself. Defaults to
   * on. Grouped lists set this per group so only the group in view stays fresh,
   * instead of running one poll per group.
   */
  pollEnabled?: boolean;
  /**
   * Turn the query off entirely. Defaults to on. Lets a consumer run the single
   * query and the per-group queries side by side, keeping only the active mode's
   * requests live.
   */
  enabled?: boolean;
};

export function useQuerySessionsList(
  params: UseQuerySessionsListParams,
  options: UseQuerySessionsListOptions = {}
) {
  const token = getSessionDataConfig().useAuthToken?.();
  const { isDesktopOnlineReady, isUserLoaded } =
    getSessionDataConfig().useSessionListEnvironment();
  // Readiness gate: is a user loaded? The request identifies the caller from the
  // bearer token (no user id is sent), so this only needs to know a user is
  // present — not which one. It's a presence check rather than `kinde_user_id`
  // because that field can be empty for an already-loaded user, which would
  // leave the list unfetched. The environment flag is that presence boolean.
  const enabled =
    Boolean(token && isUserLoaded && isDesktopOnlineReady) && (options.enabled ?? true);
  const cachedInitialData = !isDesktopOnlineReady
    ? readDesktopSessionsListCache(params)
    : undefined;

  const query = useInfiniteQuery({
    queryKey: buildSessionsListQueryKey(params),
    placeholderData: keepPreviousData,
    initialData: cachedInitialData,
    // Match the first-page poll cadence: switching back to a scope viewed within
    // this window serves the cached pages instantly with no refetch, while the
    // active scope stays fresh via `useFirstPagePoll`. A scope cached longer than
    // this refetches in the background behind `keepPreviousData` (no blank flash).
    staleTime: SESSIONS_REFETCH_INTERVAL,
    // SCR-4338: on focus/reconnect this would refetch every loaded page × every
    // grouped list — the exact fan-out MUL-1668 removed. Older pages are immutable
    // history; the first-page poll keeps page 0 fresh.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    queryFn: async ({ pageParam }): Promise<SessionListResponse> => {
      const response = await getSessionsListV2(
        buildSessionsListRequest(params, pageParam as string | undefined)
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.next_cursor ?? undefined;
    },
    initialPageParam: undefined as string | undefined,
  });

  useFirstPagePoll(params, enabled && (options.pollEnabled ?? true));

  // `params` is reconstructed inline by the caller every render, so listing it
  // as a dependency would re-run this effect (and its localStorage read + parse
  // + stringify + write) on every render. Hold it in a ref — same pattern as
  // `useFirstPagePoll`'s query-key ref — and depend only on the stable values
  // that actually gate a write.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    if (!isDesktopOnlineReady || !query.isSuccess || query.isPlaceholderData) return;
    writeDesktopSessionsListCache(paramsRef.current, query.data);
  }, [isDesktopOnlineReady, query.data, query.isPlaceholderData, query.isSuccess]);

  return query;
}

// Cache identities are single-site in `@heidi/session/contracts` (ARCFE-1001).
// These were DOUBLE-DECLARED here — the literals agreed, but two independent
// declarations of one cache key is an unguarded drift risk: a change to either
// side splits writes and reads across two caches. Imported (not just
// re-exported) because this module uses them locally too.
export { GET_SESSIONS_LIST_FIRST_PAGE };
